import { useCallback, useEffect, useRef, useState } from "react";
import apiUrls from "src/api";
import type {
  ScenariaSkyboxPromptPreviewScene,
  ScenariaSkyboxPromptPreviewTrunk,
} from "src/model/model";

type PreviewStatus = "idle" | "loading" | "error" | "ready";

interface CachedPreview {
  scenes: ScenariaSkyboxPromptPreviewScene[];
  trunk: ScenariaSkyboxPromptPreviewTrunk | null;
}

interface UseSkyboxPromptPreviewResult {
  status: PreviewStatus;
  scenes: ScenariaSkyboxPromptPreviewScene[];
  trunk: ScenariaSkyboxPromptPreviewTrunk | null;
  retry: () => void;
  refresh: () => void;
}

// Lazy fetch of the read-only skybox-prompt preview (Phase 3 step 2, D4).
// Fires ONLY when `active` is true (the Blockade section is expanded) so users
// who never open it pay nothing. Re-fetches when `planStamp` changes (pass
// plan.lastRefinedAt ?? plan.generatedAt) and caches per conversationId+stamp
// so collapse/re-expand doesn't refetch. Never blocking: an error surfaces as
// status "error" (the section shows a retry button); generation stays possible.
const useSkyboxPromptPreview = (
  conversationId: number | undefined,
  planStamp: string | undefined,
  active: boolean,
): UseSkyboxPromptPreviewResult => {
  const [status, setStatus] = useState<PreviewStatus>("idle");
  const [scenes, setScenes] = useState<ScenariaSkyboxPromptPreviewScene[]>([]);
  const [trunk, setTrunk] = useState<ScenariaSkyboxPromptPreviewTrunk | null>(null);
  // Cache keyed by conversationId+planStamp so a re-expand reuses the result.
  const cacheRef = useRef<Record<string, CachedPreview>>({});
  // Bumped by retry() to force a re-fetch after an error.
  const [attempt, setAttempt] = useState(0);

  const cacheKey = conversationId != null ? `${conversationId}:${planStamp ?? ""}` : "";

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  // Cache-busting refetch for when the server-side plan changed without a new
  // planStamp (e.g. a successful retry-image-descriptions patched the trunk).
  const refresh = useCallback(() => {
    delete cacheRef.current[cacheKey];
    setAttempt((n) => n + 1);
  }, [cacheKey]);

  useEffect(() => {
    if (!active || conversationId == null) return undefined;

    const cached = cacheRef.current[cacheKey];
    if (cached) {
      setScenes(cached.scenes);
      setTrunk(cached.trunk);
      setStatus("ready");
      return undefined;
    }

    let cancelled = false;
    setStatus("loading");

    void (async () => {
      try {
        const jwt = localStorage.getItem("wixar.auth.jwt");
        const resp = await fetch(apiUrls.gpt.planMode.skyboxPromptPreview(conversationId), {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (!resp.ok) throw new Error(`skybox-prompt-preview ${resp.status}`);
        const json = await resp.json();
        const next: CachedPreview = {
          scenes: json.scenes || [],
          trunk: json.trunk || null,
        };
        if (cancelled) return;
        cacheRef.current[cacheKey] = next;
        setScenes(next.scenes);
        setTrunk(next.trunk);
        setStatus("ready");
      } catch {
        if (cancelled) return;
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
    // attempt is a deliberate re-fetch trigger (retry after error / refresh).
  }, [active, conversationId, cacheKey, attempt]);

  return { status, scenes, trunk, retry, refresh };
};

export default useSkyboxPromptPreview;
